import { Component, OnInit, DoCheck, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { SessionService } from '../shared/services/session.service';
import { AppointmentService } from '../shared/services/appointment.service';
import { ModalService } from '../shared/modals/modal.service';
import { Appointment } from '../shared/models/appointment.model';
import { ProgressTrackerService } from '../shared/services/progress-tracker.service';
import { SpinnerService } from '../shared/spinner/spinner.service';
import { RedirectService } from '../shared/services/redirect.service';
import { Url } from '../shared/models/url/url.model';
import { User } from '../shared/models/user.model';
import { environment } from '../../environments/environment';

/**
 * This class represents the lazy loaded ScheduleInterviewComponnet.
 */
@Component({
    templateUrl: 'schedule-interview.component.html',
    standalone: false
})
export class ScheduleInterviewComponent implements OnInit, DoCheck, OnDestroy {

    public nextCaption: string;
    public stepNo: number;
    public inReview: boolean = false;
    public service: string = '';

    private routeName: string;
    private back: string;
    private next: string;
    private urlPrefix: string = '/schedule-interview/';
    // private sessionSubscription: Subject<any>;
    private sessionSubscription:Subscription;

    constructor(private route: ActivatedRoute,
                private router: Router,
                private modalService: ModalService,
                private spinnerService: SpinnerService,
                private sessionService: SessionService,
                private appointmentService: AppointmentService,
                private progressTrackerService:ProgressTrackerService,
                private redirectService:RedirectService,
                private changeDetector: ChangeDetectorRef,
                private locationService: Location) {}

    ngOnInit() {
        this.setRouteName();

        // lets make sure our session is set up properly
        this.setSession();
    }

    ngDoCheck() {
        this.setRouteName();
    }

    ngOnDestroy() {
        if(this.sessionSubscription) {
            this.sessionSubscription.unsubscribe();
        }
    }

    public submitAppointment() {
        this.spinnerService.show();

        if(this.sessionService.requireReason) {
            if(!this.sessionService.reason) {
                this.spinnerService.hide();
                this.modalService.warn('ERROR_MESSAGES.REASON_REQUIRED');
                return;
            }
        }

        // tie in rest of data to session appointment
        this.sessionService.appointment.startTimestamp = this.sessionService.appointmentSlot.startTimestamp;
        this.sessionService.appointment.endTimestamp = this.sessionService.appointmentSlot.endTimestamp;
        this.sessionService.appointment.duration = this.sessionService.appointmentSlot.duration;

        if (this.sessionService.appointmentType === Appointment.TYPE_CREATE) {
            // this is a new appointment lets create it
            if(this.sessionService.finalizeAppointmentExternally) {
                let url:Url = this.redirectService.getReturnUrl(RedirectService.WILL_SCHEDULE);
                window.location.href = url.toString();
            } else {
                this.appointmentService.createAppointment(this.sessionService.appointment)
                    .subscribe(
                        res => {
                            this.spinnerService.hide();
                            if (res !== null) {
                                let url:Url = this.redirectService.getReturnUrl(RedirectService.SCHEDULED);
                                window.location.href = url.toString();
                            } else {
                                this.modalService.warn('SCHEDULE_INTERVIEW.APPOINTMENT_TAKEN');
                            }
                        },
                        error => {
                            this.spinnerService.hide();
                            this.modalService.alert('ERROR_MESSAGES.SERVICE_ERROR');
                        }
                    );
            }

        } else {
            // this is a reschedule update appointment
            if(this.sessionService.finalizeAppointmentExternally) {
                let url:Url = this.redirectService.getReturnUrl(RedirectService.WILL_RESCHEDULE);
                window.location.href = url.toString();
            } else {
                this.appointmentService.updateAppointment(this.sessionService.appointment)
                .subscribe(
                    res => {
                        this.spinnerService.hide();
                        if (res) {
                            let url:Url = this.redirectService.getReturnUrl(RedirectService.RESCHEDULED);
                            window.location.href = url.toString();
                        } else {
                            this.modalService.warn('SCHEDULE_INTERVIEW.APPOINTMENT_TAKEN');
                        }
                    },
                    error => {
                        this.spinnerService.hide();
                        this.modalService.alert('ERROR_MESSAGES.SERVICE_ERROR');
                    }
                );
            }
        }
    }

    public goBack() {
        this.inReview = false;
        this.router.navigate([this.urlPrefix + this.back]);
    }

    public canGoBack(): boolean {
        if (this.routeName === 'location' ||
            (this.routeName === 'date' && this.sessionService.remoteAllowed)) {
            return false;
        } else {
            return true;
        }
    }

    public cancel() {
        if (!this.sessionService.viewOnly) {
            this.modalService.confirm('MODAL.CANCEL_CONFIRM')
                .then(
                    res => {
                        if (res) {
                            if (this.sessionService.returnUrl) {
                                let url:Url = this.redirectService.getReturnUrl(RedirectService.ABORTED);
                                window.location.href = url.toString();
                            } else {
                                this.router.navigate(['/']);
                            }
                        }
                    }
                );
        } else {
            if (this.sessionService.returnUrl) {
                window.location.href = this.sessionService.returnUrl;
            } else {
                this.router.navigate(['/']);
            }
        }
    }

    private setRouteName() {
        this.route.children[0].url.subscribe(childrouteName => {
            this.routeName = childrouteName.join('');
            switch(this.routeName) {
                case 'location':
                    this.next = 'date';
                    this.back = '';
                    this.stepNo = 1;
                    this.nextCaption = 'SCHEDULE_INTERVIEW.CHOOSE_LOCATION';
                    break;
                case 'date':
                    this.next = 'time';
                    this.back = 'location';
                    this.stepNo = 2;
                    this.nextCaption = 'SCHEDULE_INTERVIEW.CHOOSE_DATE';
                    break;
                case 'time':
                    this.next = 'review-submit';
                    this.back = 'date';
                    this.stepNo = 3;
                    if (!this.sessionService.viewOnly) {
                        this.nextCaption = 'SCHEDULE_INTERVIEW.CHOOSE_TIME';
                    } else {
                        this.nextCaption = 'SCHEDULE_INTERVIEW.BLANK';
                        this.inReview = true;
                    }
                    break;
                case 'schedule-remote':
                    this.next = 'date';
                    this.back = '';
                    this.stepNo = 1;
                    this.nextCaption = 'SCHEDULE_INTERVIEW.CHOOSE_LOCATION';
                    break;
                case 'review-submit':
                    this.next = '';
                    this.back = 'time';
                    this.stepNo = 4;
                    this.inReview = true;
                    this.nextCaption = 'SCHEDULE_INTERVIEW.BLANK';
                    break;
            }

            this.progressTrackerService.step = this.stepNo;
            this.changeDetector.detectChanges();

        });
    }

    private setSession() {
        // lets get our query params
        this.route.queryParams.subscribe( params => {
            let userID = params['zone'];
            let appointmentID = params['appointmentID'];
            let service = params['service'];

            let returnUrl = null;
            let tempReturnUrl = params['returnUrl'];
            if (tempReturnUrl) {
                let returnURLsObj: any = environment.returnUrls;
                if (returnURLsObj.hasOwnProperty(tempReturnUrl)) {
                    returnUrl = returnURLsObj[tempReturnUrl];
                } else {
                    // if the return url is invalid route to home.
                    this.router.navigate(['/']);
                }
            }

            let finExt = params['finExt'];
            let locationID = params['locationID'];
            let token = params['token'];
            let appCode = params['appCode'];
            let type = params['type'];
            let viewOnly = params['vo'];
            let effective = params['effective'];
            let requireReason = params['reqReason'];
            let lang = params['lang'];
            const remoteAllowed = params['remoteAllowed'];

            if (lang) {
                this.sessionService.lang = lang;
            }
            if (effective) {
                this.sessionService.effectiveDate = effective === 'true';
            }
            if (viewOnly) {
                this.sessionService.viewOnly = viewOnly === 'true';
            }
            if(finExt) {
                this.sessionService.finalizeAppointmentExternally = finExt;
            }
            if (service) {
                this.sessionService.service = service;
                this.service = ': ' + this.sessionService.service;
            } else if (this.sessionService.service) {
                this.service = ': ' + this.sessionService.service;
            }
            if (returnUrl) {
                this.sessionService.returnUrl = returnUrl;
            }
            if (locationID) {
                this.sessionService.locationID = Number(locationID);
            }
            if (!this.sessionService.authenticatedViaCookie) {
                if (token) {
                    this.sessionService.token = token;
                } else if (!this.sessionService.token && !this.sessionService.viewOnly && !this.sessionService.authenticatedViaCookie) {
                    // if the user doesn't have a token they don't belong here
                    // this check will need to move to a route gaurd
                    this.router.navigate(['/']);
                }
            }
            if (requireReason) {
                this.sessionService.requireReason = requireReason;
            }
            if (type) {
                this.sessionService.appointmentType = type;
            }

            if (remoteAllowed) {
                this.sessionService.remoteAllowed = remoteAllowed === 'true';
            }

            if (userID) {
                this.spinnerService.show();
                let user:User = new User();
                user.id = userID;

                if (appCode) {
                    user.name = appCode;
                }

                if (user !== null) {
                    this.sessionService.user = user;
                    if (type === Appointment.TYPE_UPDATE && appointmentID && locationID) {
                        // pull the app code apart to get the application ID for display purposes
                        let parts = user.name.split('-');
                        if (parts.length === 2) {
                            user.name = parts[1];
                        }
                        this.appointmentService.getAppointment(locationID, appointmentID, appCode)
                            .subscribe(
                                appointment => {
                                    if (appointment !== null) {
                                        // Added because getAppointmet service would not return the appCode
                                        if (!appointment.applicationCode) {
                                            appointment.applicationCode = appCode;
                                        }

                                        this.sessionService.appointment = appointment;
                                        if (Date.parse(appointment.startTime)) {
                                            // tslint:disable-next-line:max-line-length
                                            this.sessionService.appointmentDate = new Date(appointment.startTime);
                                        }
                                        this.sessionService.location = appointment.location;
                                        this.sessionService.appointment.user = this.sessionService.user;
                                        this.sessionSubscription = this.sessionService.proceedToNextStep.subscribe(
                                            (res: boolean) => {
                                                if (res) {
                                                    this.router.navigate([this.urlPrefix + this.next]);
                                                }
                                            }
                                        );
                                        this.spinnerService.hide();

                                        this.sessionService.isSessionInitialized = true;
                                        this.sessionService.sessionInitialized.emit(true);

                                    } else {
                                        this.spinnerService.hide();
                                        this.router.navigate(['/']);
                                    }
                                },
                                error => {
                                    this.spinnerService.hide();
                                    this.router.navigate(['/']);
                                }
                            );
                    } else if (type === Appointment.TYPE_DELETE && appCode) {
                        this.appointmentService.getAppointmentByCode(appCode)
                            .subscribe(
                                appointment => {
                                    if (appointment !== null) {
                                        this.sessionService.appointment = appointment;
                                        if (Date.parse(appointment.startTime)) {
                                            this.sessionService.appointmentDate = new Date(appointment.startTime);
                                        }
                                        this.sessionService.location = appointment.location;
                                        this.sessionService.appointment.user = this.sessionService.user;
                                    } else {
                                        // we didn't get back the appointment
                                        this.spinnerService.hide();
                                        this.router.navigate(['/']);
                                    }
                                    this.sessionSubscription = this.sessionService.proceedToNextStep.subscribe(
                                        (res: boolean) => {
                                            if (res) {
                                                this.router.navigate([this.urlPrefix + this.next]);
                                            }
                                        }
                                    );
                                    this.spinnerService.hide();
                                },
                                error => {
                                    this.spinnerService.hide();
                                    this.router.navigate(['/']);
                                }
                            );
                    } else if (type === Appointment.TYPE_CREATE && appCode) {
                        this.sessionService.appointment = new Appointment();
                        this.sessionService.appointment.user = user;
                        this.sessionService.appointment.applicationCode = appCode;
                        this.sessionSubscription = this.sessionService.proceedToNextStep.subscribe(
                            (res: boolean) => {
                                if (res) {
                                    this.router.navigate([this.urlPrefix + this.next]);
                                }
                            }
                        );
                        this.sessionService.isSessionInitialized = true;
                        this.sessionService.sessionInitialized.emit(true);

                        this.spinnerService.hide();
                    } else {
                        this.spinnerService.hide();
                        this.router.navigate(['/']);
                    }
                } else {
                    // The service didn't find this user lets send them to the home page
                    this.spinnerService.hide();
                    this.router.navigate(['/']);
                }

                if (this.sessionService.isUserLoggedIn()) {
                    this.locationService.replaceState('');
                }

            } else if (this.sessionService.user || this.sessionService.viewOnly) {
                // we are good
                if (!this.sessionSubscription) {
                    this.sessionSubscription = this.sessionService.proceedToNextStep.subscribe(
                        (res: boolean) => {
                            if (res) {
                                this.router.navigate([this.urlPrefix + this.next]);
                            }
                        }
                    );

                    this.sessionService.isSessionInitialized = true;
                    this.sessionService.sessionInitialized.emit(true);
                }
            } else {
                // we are not good kick the user out of here
                this.router.navigate(['/']);
            }
        });
    }

}
